import { useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PhoneInput } from '@/components/ui/phoneInput'
import { isProduction } from '@/config/config'
import { usePhoneField } from '@/hooks/usePhoneField'
import { createCustomer } from '../../../../models/user'

interface AddCustomerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const RANDOM_FIRST_NAMES = [
  'James',
  'Mary',
  'John',
  'Patricia',
  'Robert',
  'Jennifer',
  'Michael',
  'Linda',
  'David',
  'Elizabeth',
]
const RANDOM_LAST_NAMES = [
  'Smith',
  'Johnson',
  'Williams',
  'Brown',
  'Jones',
  'Garcia',
  'Miller',
  'Davis',
  'Rodriguez',
  'Martinez',
]

function sample(values: string[]): string {
  return values[Math.floor(Math.random() * values.length)]
}

export function AddCustomerDialog({ open, onOpenChange }: AddCustomerDialogProps) {
  const navigate = useNavigate()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [loading, setLoading] = useState(false)
  const phoneField = usePhoneField()

  function reset() {
    setFirstName('')
    setLastName('')
    setEmail('')
    setPhone('')
    phoneField.clear()
  }

  function fillRandomData() {
    setFirstName(sample(RANDOM_FIRST_NAMES))
    setLastName(sample(RANDOM_LAST_NAMES))
    setEmail(`dev+${uuidv4()}@cai.ai`)
    setPhone('')
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!phoneField.check(phone)) return
    setLoading(true)
    try {
      const result = await createCustomer({ firstName, lastName, email, phone })
      toast.success('Customer created successfully')
      reset()
      onOpenChange(false)
      navigate(`/insure/internal/customers/${result.orgId}`)
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to create customer'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        onOpenChange(value)
        if (!value) reset()
      }}
    >
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add Customer</DialogTitle>
            <DialogDescription>
              Create a new customer. They sign in with a code sent to their email.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="customer-first-name">First Name</Label>
                <Input
                  id="customer-first-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="customer-last-name">Last Name</Label>
                <Input
                  id="customer-last-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="customer-email">Email</Label>
              <Input
                id="customer-email"
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>Phone number</Label>
              <PhoneInput
                value={phone}
                onChange={(value) => setPhone(value || '')}
                error={!!phoneField.error}
              />
              <FieldError message={phoneField.error} />
            </div>
          </div>
          <DialogFooter className="mt-6">
            {!isProduction && (
              <Button type="button" variant="link" className="sm:mr-auto" onClick={fillRandomData}>
                Fill random data
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !firstName || !lastName || !email}
              loading={loading ? 'Creating...' : undefined}
            >
              Create Customer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
